import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import CookieBanner from './components/cookieBanner/CookieBanner';
import { useScrollToHash } from './hooks/useScrollToHash';
import ContactUs from './pages/contactUs/ContactUs';
import Home from './pages/home/Home';
import DataDeletion from './pages/legal/DataDeletion';
import LearnMore from './pages/learnMore/LearnMore';
import LearnMoreLanding from './pages/learnMore/LearnMoreLanding';
import ClubMobileApp from './pages/learnMore/clubMobileApp/ClubMobileApp';
import ContentManagement from './pages/learnMore/contentManagement/ContentManagement';
import CustomisedClubWebsite from './pages/learnMore/customisedClubWebsite/CustomisedClubWebsite';
import HandsOnSupport from './pages/learnMore/handsOnSupport/HandsOnSupport';
import MembershipManagement from './pages/learnMore/membershipManagement/MembershipManagement';
import SeamlessPaymentsRefunds from './pages/learnMore/seamlessPaymentsRefunds/SeamlessPaymentsRefunds';
import AppGet from './pages/appGet/AppGet';
import AppRedirect from './pages/appRedirect/AppRedirect';
import OAuthSuccess from './pages/oauth/OAuthSuccess';
import HelpCentreLayout from './pages/helpCentre/HelpCentreLayout';

// The help centre pulls in react-markdown/micromark/remark/rehype, ~373 KB
// (~116 KB gzipped) that nothing outside /help-centre links to yet. Loading
// it lazily keeps that weight off every other route, in particular the
// marketing homepage.
const HelpCentreHub = React.lazy(() => import('./pages/helpCentre/HelpCentreHub'));
const HelpArticle = React.lazy(() => import('./pages/helpCentre/HelpArticle'));

// HelpCentreLayout itself stays a static import: it only wraps the already
// site-wide Heading/Footer, so it costs nothing extra on other pages, and
// reusing it here keeps the header/footer chrome stable (no flash-in) while
// the lazy chunk above is still downloading.
const HelpCentreFallback = (): React.JSX.Element => (
  <HelpCentreLayout>
    <div className="hc-container" aria-busy="true" />
  </HelpCentreLayout>
);

export default function App(): React.JSX.Element {
  useScrollToHash();

  return (
    <>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/oauth" element={<OAuthSuccess />} />
      <Route path="/app/get" element={<AppGet />} />
      <Route path="/app/*" element={<AppRedirect />} />
      <Route path="/learn-more">
        <Route index element={<LearnMoreLanding />} />
        <Route element={<LearnMore />}>
          <Route path="customised-club-website" element={<CustomisedClubWebsite />} />
          <Route path="content-management" element={<ContentManagement />} />
          <Route path="club-mobile-app" element={<ClubMobileApp />} />
          <Route path="membership-management" element={<MembershipManagement />} />
          <Route path="seamless-payments-refunds" element={<SeamlessPaymentsRefunds />} />
          <Route path="hands-on-support" element={<HandsOnSupport />} />
        </Route>
      </Route>
      <Route path="/help-centre">
        <Route
          index
          element={
            <Suspense fallback={<HelpCentreFallback />}>
              <HelpCentreLayout>
                <HelpCentreHub />
              </HelpCentreLayout>
            </Suspense>
          }
        />
        <Route
          path=":slug"
          element={
            <Suspense fallback={<HelpCentreFallback />}>
              <HelpArticle />
            </Suspense>
          }
        />
      </Route>
    </Routes>
    <CookieBanner />
    </>
  );
}
